/**
 * Join requests and responses (app-specific, see NIP.md): an applicant's
 * "who I am / why I want to join" form giftwrapped to the moderator as a
 * kind 8031 rumor, and the moderator's decline notice back as kind 9583.
 * Approval is native Concord: a CORD-05 §6 Direct Invite (kind 3313).
 */

import type { NostrEvent } from "@nostrify/nostrify";

import { JOIN_REQUEST_KIND, JOIN_RESPONSE_KIND } from "./config";
import { createGiftWrap, unwrapGiftWrap, type GiftSigner } from "./stream";
import type { JoinRequest, JoinResponse } from "./types";

export function parseJoinRequest(content: string): JoinRequest | null {
  try {
    const r = JSON.parse(content) as Record<string, unknown>;
    const name = typeof r.name === "string" ? r.name.slice(0, 200) : "";
    const about = typeof r.about === "string" ? r.about.slice(0, 4000) : "";
    const why = typeof r.why === "string" ? r.why.slice(0, 4000) : "";
    const links = typeof r.links === "string" ? r.links.slice(0, 1000) : "";
    const confirmations = Array.isArray(r.confirmations)
      ? r.confirmations.filter((c): c is string => typeof c === "string").slice(0, 8)
      : [];
    if (!about && !why) return null;
    return { name, about, why, links, confirmations };
  } catch {
    return null;
  }
}

/** Giftwrap a join request to the moderator. */
export async function createJoinRequestEvent(opts: {
  signer: GiftSigner;
  applicantPub: string;
  moderatorPub: string;
  request: JoinRequest;
}): Promise<NostrEvent> {
  return createGiftWrap({
    signer: opts.signer,
    senderPub: opts.applicantPub,
    recipientPub: opts.moderatorPub,
    rumorKind: JOIN_REQUEST_KIND,
    content: JSON.stringify(opts.request),
    outerTags: [["k", String(JOIN_REQUEST_KIND)]],
  });
}

export interface ReceivedJoinRequest {
  applicant: string;
  request: JoinRequest;
  rumorId: string;
  wrapId: string;
  createdAt: number;
}

/** Unwrap a join-request giftwrap addressed to the moderator. */
export async function unwrapJoinRequest(
  wrap: NostrEvent,
  signer: GiftSigner,
): Promise<ReceivedJoinRequest | null> {
  const opened = await unwrapGiftWrap(wrap, signer);
  if (!opened) return null;
  if (opened.rumor.kind !== JOIN_REQUEST_KIND) return null;
  const request = parseJoinRequest(opened.rumor.content);
  if (!request) return null;
  return {
    applicant: opened.rumor.pubkey,
    request,
    rumorId: opened.rumor.id,
    wrapId: wrap.id,
    createdAt: opened.rumor.created_at,
  };
}

/** Giftwrap a decline notice back to an applicant. */
export async function createJoinResponseEvent(opts: {
  signer: GiftSigner;
  moderatorPub: string;
  recipientPub: string;
  message: string;
}): Promise<NostrEvent> {
  const response: JoinResponse = { status: "declined", message: opts.message.slice(0, 2000) };
  return createGiftWrap({
    signer: opts.signer,
    senderPub: opts.moderatorPub,
    recipientPub: opts.recipientPub,
    rumorKind: JOIN_RESPONSE_KIND,
    content: JSON.stringify(response),
    outerTags: [["k", String(JOIN_RESPONSE_KIND)]],
  });
}

export interface ReceivedJoinResponse {
  from: string;
  response: JoinResponse;
  wrapId: string;
  createdAt: number;
}

/** Unwrap a join-response giftwrap addressed to the applicant. */
export async function unwrapJoinResponse(
  wrap: NostrEvent,
  signer: GiftSigner,
): Promise<ReceivedJoinResponse | null> {
  const opened = await unwrapGiftWrap(wrap, signer);
  if (!opened) return null;
  if (opened.rumor.kind !== JOIN_RESPONSE_KIND) return null;
  try {
    const r = JSON.parse(opened.rumor.content) as Record<string, unknown>;
    if (r.status !== "declined") return null;
    return {
      from: opened.rumor.pubkey,
      response: {
        status: "declined",
        message: typeof r.message === "string" ? r.message : "",
      },
      wrapId: wrap.id,
      createdAt: opened.rumor.created_at,
    };
  } catch {
    return null;
  }
}
